import { useState } from "react";
import { ShieldCheck, AlertTriangle, Calculator, ChevronDown } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { useTankDrops } from "@/components/drop/TankHistory";
import { formatGal, formatToEighths, tankKey } from "@/lib/tank";

const ENDS = { flat: "flat (steel)", dome: "rounded (fiberglass)" };

function level(r, record, t) {
  if (r.source === "chart") {
    if (!record.n) return { tone: "good", text: t("Real chart for this tank") };
    return record.max <= 0.5
      ? { tone: "good", text: t("Real chart · checked on {n} drop(s) · within {x}", { n: record.n, x: formatToEighths(record.max) }) }
      : { tone: "warn", text: t("Real chart · off by up to {x} on {n} drop(s). Check the chart.", { n: record.n, x: formatToEighths(record.max) }) };
  }
  if (r.source === "console") return { tone: "good", text: t("Starting gallons from the console printout") };
  if (record.n >= 3 && record.max <= 0.5) return { tone: "good", text: t("Checked on {n} drops · within {x}", { n: record.n, x: formatToEighths(record.max) }) };
  if (record.n > 0) return { tone: record.max <= 0.5 ? "good" : "warn", text: t("Checked on {n} drop(s) · off by up to {x}", { n: record.n, x: formatToEighths(record.max) }) };
  return { tone: "warn", text: t("Estimate from tank size · stick to confirm") };
}

export function TrustPanel({ s, r, showDrop = false }) {
  const t = useT();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const onSite = user && s.activeSiteId && s.activeKey === tankKey(s);
  const { record } = useTankDrops(s, !!onSite, r.source === "chart");
  const lv = level(r, onSite ? record : { n: 0, max: 0 }, t);
  const Icon = lv.tone === "good" ? ShieldCheck : AlertTriangle;

  return (
    <div className="trust" data-testid="trust-panel">
      <div className="trust-top">
        <span className={`trust-level trust-${lv.tone}`} data-testid="trust-level"><Icon size={14} />{lv.text}</span>
        <span className="trust-noai" data-testid="trust-no-ai"><Calculator size={12} />{t("No AI · tank math")}</span>
      </div>
      {(showDrop ? r.beyondChart : r.beyondStart) && <div className="form-error" data-testid="trust-beyond">{t("This is past the end of the station chart. Stick the tank before you drop.")}</div>}
      <button type="button" className="trust-how" data-testid="trust-how-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
        {t("How this was worked out")}<ChevronDown size={14} className={open ? "rot" : ""} />
      </button>
      {open && (
        <ol className="trust-steps" data-testid="trust-steps">
          {r.source === "chart" ? (
            <li>{t("Your stick {s} is between chart lines {a} = {ag} gal and {b} = {bg} gal, so the tank has {g} gal.", {
              s: formatToEighths(s.startInches), a: formatToEighths(r.chartRows[0][0]), ag: formatGal(r.chartRows[0][1]),
              b: formatToEighths(r.chartRows[1][0]), bg: formatGal(r.chartRows[1][1]), g: formatGal(r.startGallons) })}</li>
          ) : r.source === "console" ? (
            <li>{t("The console printout says {g} gal at {s}. The prediction starts from that, then uses a {c} gal, {d}\" tank shape for the stick after.", {
              g: formatGal(r.startGallons), s: formatToEighths(s.startInches), c: formatGal(s.capacity), d: s.diameter })}</li>
          ) : (
            <>
              <li>{t("Tank: {c} gal, {d}\" across, {e} ends.", { c: formatGal(s.capacity), d: s.diameter, e: t(ENDS[s.ends] || s.ends) })}</li>
              <li>{t("At {s} of fuel, the shape of that tank holds {g} gal ({p}% full).", { s: formatToEighths(s.startInches), g: formatGal(r.startGallons), p: r.startPct.toFixed(1) })}</li>
              <li className="trust-note">{t("Real tanks can differ from the maker's size by 1–3%. Add the station's own chart to this tank to remove that.")}</li>
            </>
          )}
          {showDrop && <li>{t("{a} gal + {b} gal dropped = {c} gal, which is a stick of {i} ({p}% full).", {
            a: formatGal(r.startGallons), b: formatGal(s.dropGallons), c: formatGal(r.finalGallons), i: formatToEighths(r.finalInches), p: r.finalPct.toFixed(1) })}</li>}
          <li>{t("90% = {a} gal · 95% = {b} gal of {c} gal.", { a: formatGal(r.cap90), b: formatGal(r.cap95), c: formatGal(s.capacity) })}</li>
        </ol>
      )}
    </div>
  );
}
